import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check, CheckCircle2, Copy, Crop, ExternalLink, Eye, ImageOff, Loader2, RotateCcw, ScanSearch, Sparkles, XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { Toast } from '../../../components/design-system/common/Toast';
import { PageContainer } from '../../../common/components/PageContainer';
import {
  DemoPlacement, DemoProduct, DemoProductStatus, DemoScan, DemoTiles, GeneratedDemo, SlugStatus, apiErrorMessage, useDemoSitesApi,
} from '../../../common/hooks/use-demo-sites-api.hook';
import type { PhotoAnalysisResult } from '../../../modules/vendor/edit-product-v3/api/productV2Api';
import { patternLabel } from '../../../modules/vendor/edit-product-v3/components/shared/media/BuildFromPhotosDialog';
import { CroppedPreview, PhotoCrop, PhotoCropDialog } from './PhotoCropDialog';
import { VisualizerPlacementPicker, suggestedSpot } from './VisualizerPlacementPicker';
// Our own look at a new demo is never counted as a prospect's visit.
import { withTeamTag } from '../../../common/services/preview-tag.service';

const DOMAIN = 'ardealy.link';
const MAX_IMAGES = 8;
const STATUS_POLL_MS = 4000;
const TILES_POLL_MS = 2500;
/** Build from photos reads a dozen photos in well under a minute; past this, something is stuck. */
const TILES_GIVE_UP_MS = 4 * 60 * 1000;

/**
 * A surface's tiles, cut from its chosen photos by Build from photos (the
 * vendor editor's AI action). `photos` is what they were cut from, so a change
 * of photos or crops can say the tiles are out of date.
 */
type TilesRun =
  | { phase: 'running'; photos: string }
  | { phase: 'result'; photos: string; result: PhotoAnalysisResult }
  | { phase: 'error'; photos: string; error: string };

const isPageUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

/** The shop's name out of its address — "www.maison-carrelage.fr" → "maison-carrelage". */
const slugFromUrl = (value: string): string => {
  try {
    const parts = new URL(value.trim()).hostname.replace(/^www\d?\./, '').split('.');
    const name = parts.length > 1 ? parts[parts.length - 2] : parts[0];
    return name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 63);
  } catch {
    return '';
  }
};

type Step = 'input' | 'scanning' | 'review' | 'generating' | 'done';
/** 'new' adds the product; otherwise the uuid of a product already in the demo catalogue. */
type ProductChoice = 'new' | string;

/**
 * Super-admin demo generator — no second sign-in needed.
 *
 * 1. A product page and a name space.
 * 2. The page is read with the "Add product with AI" extractor, on the
 *    Ardealy demo vendor's behalf; the product is checked here and its photos
 *    chosen by hand. A product already in the demo catalogue for this page can
 *    be used instead.
 * 3. Generate adds it to the demo catalogue, copies the page into a static
 *    site wired to it, and publishes <name space>.ardealy.link.
 */
export const DemoGeneratorPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useDemoSitesApi();

  const [step, setStep] = useState<Step>('input');
  const [url, setUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [brandName, setBrandName] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<SlugStatus | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);

  const [scan, setScan] = useState<DemoScan | null>(null);
  const [choice, setChoice] = useState<ProductChoice>('new');
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [familySlug, setFamilySlug] = useState('');
  /** Furniture category; `rugs` also has its width and length (cm), from which its 3D model is made. */
  const [category, setCategory] = useState('');
  const [rugWidth, setRugWidth] = useState('');
  const [rugLength, setRugLength] = useState('');
  /** Chosen photos, in the order they were chosen: the first is the thumbnail. */
  const [chosen, setChosen] = useState<string[]>([]);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  /** Crops by photo; a photo without one is used whole. */
  const [crops, setCrops] = useState<Record<string, PhotoCrop>>({});
  /** Each photo's size in pixels, so its cropped preview keeps the crop's shape. */
  const [naturalSizes, setNaturalSizes] = useState<Record<string, { w: number; h: number }>>({});
  const [cropping, setCropping] = useState<string | null>(null);
  const [tilesRun, setTilesRun] = useState<TilesRun | null>(null);
  /** The cut-outs to use, by index in the result (a seamless result uses one). */
  const [chosenTiles, setChosenTiles] = useState<Set<number>>(new Set());
  /** A surface without tiles: the chosen photos laid as its texture (none picked = the main one). */
  const [plankPhotos, setPlankPhotos] = useState<Set<string>>(new Set());
  const tilesRunId = useRef(0);

  const [placement, setPlacement] = useState<DemoPlacement>({ surface: 'button' });
  // The AI placement step (ARDE-287): on, the spot is left to the model (the rule-based one when it cannot place it).
  const [aiPlacement, setAiPlacement] = useState(true);
  const [result, setResult] = useState<GeneratedDemo | null>(null);
  const [productStatus, setProductStatus] = useState<DemoProductStatus | null>(null);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  // Suggest the name space from the shop's address until it is typed by hand.
  useEffect(() => {
    if (!slugTouched) setSlug(slugFromUrl(url));
  }, [url, slugTouched]);

  // Check the name space as it is typed.
  useEffect(() => {
    if (!slug || step === 'done') {
      setSlugStatus(null);
      return undefined;
    }
    setCheckingSlug(true);
    const timer = setTimeout(async () => {
      try {
        setSlugStatus(await api.slugStatus(slug));
      } catch {
        setSlugStatus(null);
      } finally {
        setCheckingSlug(false);
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Follow a product added by generate until the catalogue (and its seamless texture) has it.
  useEffect(() => {
    const batchId = result?.batchId;
    if (!batchId) return undefined;
    let stopped = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const status = await api.demoProductStatus(batchId, result?.prospect?._id);
        if (stopped) return;
        setProductStatus(status);
        if (status.status === 'completed' || status.status === 'failed') return;
      } catch {
        // The job record can lag the request by a moment; keep asking.
      }
      if (!stopped) timer = window.setTimeout(poll, STATUS_POLL_MS);
    };
    poll();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.batchId]);

  // Any existing site blocks the name space — a generated demo never replaces one —
  // and so does a customer's alias: the demo's prospect is made under this name.
  const customerAlias = slugStatus?.vendorWithAlias?.kind === 'customer';
  const slugOk = !!slugStatus?.valid && !slugStatus.taken && !customerAlias && !checkingSlug && slugStatus.slug === slug;
  const urlOk = isPageUrl(url);

  const slugHint = (() => {
    if (!slug || step === 'done') return null;
    if (checkingSlug) return { text: t('SUPER_ADMIN.DEMO_GENERATOR.SLUG_CHECKING'), tone: 'muted' };
    if (!slugStatus) return null;
    if (!slugStatus.valid) return { text: slugStatus.problem, tone: 'error' };
    if (slugStatus.taken) {
      return { text: t('SUPER_ADMIN.DEMO_GENERATOR.SLUG_TAKEN', { vendor: slugStatus.takenBy || '—' }), tone: 'error' };
    }
    if (customerAlias) {
      return { text: t('SUPER_ADMIN.DEMO_GENERATOR.SLUG_CUSTOMER', { vendor: slugStatus.vendorWithAlias?.name || slug }), tone: 'error' };
    }
    if (slugStatus.vendorWithAlias?.kind === 'prospect') {
      return { text: t('SUPER_ADMIN.DEMO_GENERATOR.SLUG_PROSPECT', { url: `${slug}.${DOMAIN}`, vendor: slugStatus.vendorWithAlias.name }), tone: 'success' };
    }
    return { text: t('SUPER_ADMIN.DEMO_GENERATOR.SLUG_FREE', { url: `${slug}.${DOMAIN}` }), tone: 'success' };
  })();

  const family = useMemo(() => scan?.families.find((f) => f.slug === familySlug), [scan, familySlug]);
  const images = useMemo(
    () => (scan?.product.imageUrls || []).filter((u) => !brokenImages.has(u)),
    [scan, brokenImages],
  );
  const existing = scan?.existing || [];
  const usingExisting = choice !== 'new';
  const existingChoice = existing.find((p) => p.uuid === choice);

  const runScan = async () => {
    if (!urlOk || !slugOk) return;
    setStep('scanning');
    try {
      const res = await api.scanProductPage(url.trim(), slug);
      setScan(res);
      setName(res.product.name || '');
      setSku(res.product.sku || '');
      setFamilySlug(res.product.familySlug || (res.families.length === 1 ? res.families[0].slug : ''));
      setCategory(res.product.category || '');
      setRugWidth(res.product.size?.width ? String(res.product.size.width) : '');
      setRugLength(res.product.size?.height ? String(res.product.size.height) : '');
      // A surface's photos all go to Build from photos, which cuts the tiles out
      // of them (a sheet, a room, a tile on a table); other products keep three.
      const surface = res.families.find((f) => f.slug === res.product.familySlug)?.visualization === 'surface';
      setChosen((res.product.imageUrls || []).slice(0, surface ? MAX_IMAGES : 3));
      setBrokenImages(new Set());
      setCrops({});
      setNaturalSizes({});
      tilesRunId.current += 1;
      setTilesRun(null);
      // A product already added for this page is reused rather than duplicated.
      setChoice(res.existing.find((p) => p.sku)?.uuid || 'new');
      // The visualizer starts as a button at the suggested spot.
      setPlacement({ surface: 'button', spot: suggestedSpot(res.placement, 'button') });
      setStep('review');
    } catch (e) {
      setToast({ msg: (await apiErrorMessage(e)) || t('SUPER_ADMIN.DEMO_GENERATOR.SCAN_FAILED'), sev: 'error' });
      setStep('input');
    }
  };

  const toggleImage = (imageUrl: string) => {
    setChosen((prev) => (prev.includes(imageUrl)
      ? prev.filter((u) => u !== imageUrl)
      : prev.length >= MAX_IMAGES ? prev : [...prev, imageUrl]));
  };

  const applyCrop = (imageUrl: string, crop: PhotoCrop | null) => {
    setCrops((prev) => {
      const next = { ...prev };
      if (crop) next[imageUrl] = crop;
      else delete next[imageUrl];
      return next;
    });
    setCropping(null);
  };

  const onImageError = (imageUrl: string) => {
    setBrokenImages((prev) => new Set(prev).add(imageUrl));
    setChosen((prev) => prev.filter((u) => u !== imageUrl));
  };

  const isSurface = family?.visualization === 'surface';
  const tilesPhotos = JSON.stringify(chosen.map((u) => [u, crops[u] || null]));
  const tilesResult = tilesRun?.phase === 'result' && tilesRun.result.mode !== 'none' ? tilesRun.result : null;
  const tilesStale = !!tilesRun && tilesRun.photos !== tilesPhotos;

  const cutTiles = async () => {
    if (!scan || !chosen.length) return;
    const runId = ++tilesRunId.current;
    const photos = tilesPhotos;
    const p = scan.product;
    setTilesRun({ phase: 'running', photos });
    try {
      let job = await api.startDemoTiles({
        imageUrls: chosen,
        imageCrops: chosen.filter((u) => crops[u]).map((u) => ({ url: u, ...crops[u] })),
        size: p.size?.width && p.size?.height ? { width: p.size.width, height: p.size.height } : undefined,
        pattern: p.pattern || undefined,
        colorHex: p.color?.hex || undefined,
      });
      const started = Date.now();
      while (job.status === 'pending' || job.status === 'processing' || (job.status === 'done' && !job.result)) {
        if (Date.now() - started > TILES_GIVE_UP_MS) throw new Error(t('SUPER_ADMIN.DEMO_GENERATOR.TILES_TIMEOUT'));
        await new Promise((r) => setTimeout(r, TILES_POLL_MS));
        if (tilesRunId.current !== runId) return;
        job = await api.demoTilesStatus(job.jobId);
      }
      if (tilesRunId.current !== runId) return;
      if (job.status === 'failed' || !job.result) throw new Error(job.error || t('SUPER_ADMIN.DEMO_GENERATOR.TILES_FAILED'));
      const result = job.result;
      const offered = result.preselect?.filter((i) => i >= 0 && i < result.images.length);
      setChosenTiles(new Set(result.mode === 'seamless' ? [0] : offered?.length ? offered : result.images.map((_, i) => i)));
      setTilesRun({ phase: 'result', photos, result });
    } catch (e) {
      if (tilesRunId.current !== runId) return;
      setTilesRun({ phase: 'error', photos, error: (await apiErrorMessage(e)) || (e as Error)?.message || t('SUPER_ADMIN.DEMO_GENERATOR.TILES_FAILED') });
    }
  };

  // A new surface product's photos go to Build from photos once, as soon as
  // they are known; after that, only on request (the photos or crops changed).
  useEffect(() => {
    if (step === 'review' && isSurface && choice === 'new' && chosen.length && !tilesRun) void cutTiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isSurface, choice, chosen.length, tilesRun]);

  const toggleTile = (i: number) => {
    if (tilesResult?.mode === 'seamless') {
      setChosenTiles(new Set([i]));
      return;
    }
    setChosenTiles((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  /** What generate makes the surface from: the chosen cut-outs, or the photos when there are none. */
  const tilesForProduct = (): DemoTiles | undefined => {
    if (!isSurface || !tilesResult || tilesStale) return undefined;
    const urls = tilesResult.images.filter((_, i) => chosenTiles.has(i)).map((im) => im.url);
    if (!urls.length) return undefined;
    return {
      mode: tilesResult.mode as 'tiles' | 'seamless',
      urls,
      pattern: tilesResult.pattern || undefined,
      cellSizeCm: tilesResult.cellSizeCm || undefined,
      mirror: !!tilesResult.options?.mirror,
    };
  };

  /**
   * Without tiles, the surface is laid with the photos picked here (a tile's
   * faces, a parquet's planks) and the other chosen photos are its gallery —
   * room shots must never be laid. None picked: the main photo.
   */
  const texturePhotos = isSurface && !tilesForProduct() ? chosen.filter((u) => plankPhotos.has(u)) : [];
  const planksForProduct = (): number[] | undefined => {
    if (!isSurface || tilesForProduct()) return undefined;
    const picked = chosen.map((u, i) => (plankPhotos.has(u) ? i : -1)).filter((i) => i >= 0);
    return picked.length ? picked : [0];
  };
  const togglePlankPhoto = (imageUrl: string) => {
    setPlankPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(imageUrl)) next.delete(imageUrl); else next.add(imageUrl);
      return next;
    });
  };

  const isFurniture = !!family && family.visualization !== 'surface';
  const isRug = isFurniture && category === 'rugs';
  const rugSize = { width: Number(rugWidth), height: Number(rugLength) };
  const rugSizeOk = rugSize.width > 0 && rugSize.height > 0;
  const newProductReady = !!name.trim() && !!familySlug && chosen.length > 0 && (!isRug || rugSizeOk);
  const tilesRunning = isSurface && !usingExisting && tilesRun?.phase === 'running';
  const canGenerate = step === 'review' && slugOk && !tilesRunning && (usingExisting ? !!existingChoice?.sku : newProductReady);

  const generate = async () => {
    if (!canGenerate || !scan) return;
    setStep('generating');
    const p = scan.product;
    // With the AI step on, only the surface is sent: a spot would be kept as chosen and the model not asked.
    const placementToSend: DemoPlacement = aiPlacement ? { surface: placement.surface } : placement;
    try {
      const res = await api.generateDemo(usingExisting
        ? { url: url.trim(), slug, placement: placementToSend, aiPlacement, brandName: brandName.trim() || undefined, productUuid: choice }
        : {
          url: url.trim(),
          slug,
          placement: placementToSend,
          aiPlacement,
          brandName: brandName.trim() || undefined,
          product: {
            familySlug,
            name: name.trim(),
            sku: sku.trim() || undefined,
            brand: p.brand || undefined,
            description: p.description || undefined,
            imageUrls: chosen,
            imageCrops: chosen.filter((u) => crops[u]).map((u) => ({ url: u, ...crops[u] })),
            color: p.color?.value ? p.color : undefined,
            category: isFurniture && category ? category : undefined,
            // A rug's size is width × length; its 3D model is made to it.
            size: isRug
              ? { width: rugSize.width, height: rugSize.height, unit: 'cm' }
              : p.size?.width && p.size?.height ? p.size : undefined,
            pattern: p.pattern || undefined,
            productType: p.productType || undefined,
            applicationTypes: p.applicationTypes?.length ? p.applicationTypes : undefined,
            tiles: tilesForProduct(),
            planks: planksForProduct(),
          },
        });
      setResult(res);
      setProductStatus(null);
      setStep('done');
    } catch (e) {
      setToast({ msg: (await apiErrorMessage(e)) || t('SUPER_ADMIN.DEMO_GENERATOR.GENERATE_FAILED'), sev: 'error' });
      setStep('review');
    }
  };

  const startOver = () => {
    setStep('input');
    setUrl('');
    setSlug('');
    setBrandName('');
    setSlugTouched(false);
    setScan(null);
    setChoice('new');
    setChosen([]);
    setPlankPhotos(new Set());
    tilesRunId.current += 1;
    setTilesRun(null);
    setResult(null);
    setProductStatus(null);
  };

  const busy = step === 'scanning' || step === 'generating';

  return (
    <PageContainer breadcrumbs={[{ label: t('SUPER_ADMIN.DEMO_GENERATOR.TITLE') }]}>
      <div className="mx-auto flex max-w-3xl flex-col gap-5">
        <div>
          <h1 className="mb-1 font-heading text-2xl font-medium tracking-tight">{t('SUPER_ADMIN.DEMO_GENERATOR.TITLE')}</h1>
          <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.SUBTITLE')}</p>
        </div>

        {/* 1. Page and name space */}
        <Card>
          <CardHeader>
            <CardTitle>{t('SUPER_ADMIN.DEMO_GENERATOR.STEP_SOURCE')}</CardTitle>
            <CardDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.STEP_SOURCE_HINT')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Field>
              <FieldLabel htmlFor="gen-url">{t('SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_URL')}</FieldLabel>
              <Input
                id="gen-url"
                type="url"
                value={url}
                placeholder="https://"
                disabled={step !== 'input'}
                aria-invalid={!!url && !urlOk}
                onChange={(e) => setUrl(e.target.value)}
              />
              {!!url && !urlOk && (
                <FieldDescription className="text-destructive">{t('SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_URL_INVALID')}</FieldDescription>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="gen-slug">{t('SUPER_ADMIN.DEMO_GENERATOR.NAME_SPACE')}</FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  id="gen-slug"
                  value={slug}
                  disabled={busy || step === 'done'}
                  onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().trim()); }}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">.{DOMAIN}</span>
              </div>
              {slugHint && (
                <FieldDescription
                  className={cn(slugHint.tone === 'error' && 'text-destructive', slugHint.tone === 'success' && 'text-success')}
                >
                  {slugHint.text}
                </FieldDescription>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="gen-brand">{t('SUPER_ADMIN.DEMO_GENERATOR.BRAND_NAME')}</FieldLabel>
              <Input
                id="gen-brand"
                value={brandName}
                maxLength={80}
                disabled={busy || step === 'done'}
                onChange={(e) => setBrandName(e.target.value)}
              />
              <FieldDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.BRAND_NAME_HINT')}</FieldDescription>
            </Field>

            {(step === 'input' || step === 'scanning') && (
              <div>
                <Button variant="outline" onClick={runScan} disabled={!urlOk || !slugOk || busy}>
                  {step === 'scanning'
                    ? <Loader2 data-icon="inline-start" className="animate-spin" />
                    : <ScanSearch data-icon="inline-start" />}
                  {step === 'scanning' ? t('SUPER_ADMIN.DEMO_GENERATOR.SCANNING') : t('SUPER_ADMIN.DEMO_GENERATOR.SCAN')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. The product, checked before anything is written */}
        {scan && (step === 'review' || step === 'generating') && (
          <Card>
            <CardHeader>
              <CardTitle>{t('SUPER_ADMIN.DEMO_GENERATOR.STEP_PRODUCT')}</CardTitle>
              <CardDescription>
                {scan.prospect.exists
                  ? t('SUPER_ADMIN.DEMO_GENERATOR.STEP_PRODUCT_HINT_PROSPECT', { vendor: `${scan.prospect.name} (${scan.prospect.alias})` })
                  : t('SUPER_ADMIN.DEMO_GENERATOR.STEP_PRODUCT_HINT_NEW', { alias: scan.prospect.alias || slug })}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {existing.length > 0 && (
                <Field>
                  <FieldLabel>{t('SUPER_ADMIN.DEMO_GENERATOR.EXISTING_TITLE')}</FieldLabel>
                  <FieldDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.EXISTING_HINT')}</FieldDescription>
                  <div className="flex flex-col gap-2">
                    {existing.map((p) => (
                      <ChoiceRow key={p.uuid} selected={choice === p.uuid} disabled={busy} onSelect={() => setChoice(p.uuid)}>
                        <ExistingProduct product={p} />
                      </ChoiceRow>
                    ))}
                    <ChoiceRow selected={choice === 'new'} disabled={busy} onSelect={() => setChoice('new')}>
                      <span className="text-sm font-medium">{t('SUPER_ADMIN.DEMO_GENERATOR.ADD_NEW')}</span>
                    </ChoiceRow>
                  </div>
                  {existingChoice && !existingChoice.sku && (
                    <FieldDescription className="text-destructive">{t('SUPER_ADMIN.DEMO_GENERATOR.NO_SKU')}</FieldDescription>
                  )}
                </Field>
              )}

              {!usingExisting && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field>
                      <FieldLabel htmlFor="gen-name">{t('SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_NAME')}</FieldLabel>
                      <Input id="gen-name" value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="gen-sku">{t('SUPER_ADMIN.DEMO_GENERATOR.SKU')}</FieldLabel>
                      <Input
                        id="gen-sku"
                        value={sku}
                        disabled={busy}
                        placeholder={`demo-${slug || 'name'}`}
                        onChange={(e) => setSku(e.target.value)}
                      />
                    </Field>
                  </div>

                  <Field>
                    <FieldLabel htmlFor="gen-family">{t('SUPER_ADMIN.DEMO_GENERATOR.FAMILY')}</FieldLabel>
                    <NativeSelect
                      id="gen-family"
                      className="w-full"
                      value={familySlug}
                      disabled={busy}
                      onChange={(e) => setFamilySlug(e.target.value)}
                    >
                      <NativeSelectOption value="">{t('SUPER_ADMIN.DEMO_GENERATOR.FAMILY_PLACEHOLDER')}</NativeSelectOption>
                      {scan.families.map((f) => (
                        <NativeSelectOption key={f.slug} value={f.slug}>{f.name}</NativeSelectOption>
                      ))}
                    </NativeSelect>
                    <FieldDescription>
                      {family
                        ? t(`SUPER_ADMIN.DEMO_GENERATOR.FAMILY_${family.visualization.toUpperCase()}`)
                        : t('SUPER_ADMIN.DEMO_GENERATOR.FAMILY_UNSURE')}
                    </FieldDescription>
                  </Field>

                  {isFurniture && (
                    <Field>
                      <FieldLabel htmlFor="gen-category">{t('SUPER_ADMIN.DEMO_GENERATOR.CATEGORY')}</FieldLabel>
                      <NativeSelect
                        id="gen-category"
                        className="w-full"
                        value={category}
                        disabled={busy}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        <NativeSelectOption value="">{t('SUPER_ADMIN.DEMO_GENERATOR.CATEGORY_NONE')}</NativeSelectOption>
                        {(scan.categories || []).map((c) => (
                          <NativeSelectOption key={c.slug} value={c.slug}>{c.displayName}</NativeSelectOption>
                        ))}
                      </NativeSelect>
                      <FieldDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.CATEGORY_HINT')}</FieldDescription>
                    </Field>
                  )}

                  {isRug && (
                    <Field>
                      <FieldLabel>{t('SUPER_ADMIN.DEMO_GENERATOR.RUG_SIZE')}</FieldLabel>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          inputMode="decimal"
                          min={1}
                          aria-label={t('SUPER_ADMIN.DEMO_GENERATOR.RUG_WIDTH')}
                          placeholder={t('SUPER_ADMIN.DEMO_GENERATOR.RUG_WIDTH')}
                          value={rugWidth}
                          disabled={busy}
                          onChange={(e) => setRugWidth(e.target.value)}
                        />
                        <span className="text-muted-foreground">×</span>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min={1}
                          aria-label={t('SUPER_ADMIN.DEMO_GENERATOR.RUG_LENGTH')}
                          placeholder={t('SUPER_ADMIN.DEMO_GENERATOR.RUG_LENGTH')}
                          value={rugLength}
                          disabled={busy}
                          onChange={(e) => setRugLength(e.target.value)}
                        />
                        <span className="text-sm text-muted-foreground">cm</span>
                      </div>
                      <FieldDescription className={cn(!rugSizeOk && 'text-destructive')}>
                        {t(rugSizeOk ? 'SUPER_ADMIN.DEMO_GENERATOR.RUG_SIZE_HINT' : 'SUPER_ADMIN.DEMO_GENERATOR.RUG_SIZE_MISSING')}
                      </FieldDescription>
                    </Field>
                  )}

                  <Field>
                    <FieldLabel>{t('SUPER_ADMIN.DEMO_GENERATOR.IMAGES')}</FieldLabel>
                    <FieldDescription>
                      {t(family?.visualization === 'surface'
                        ? 'SUPER_ADMIN.DEMO_GENERATOR.IMAGES_HINT_SURFACE'
                        : isRug ? 'SUPER_ADMIN.DEMO_GENERATOR.IMAGES_HINT_RUG' : 'SUPER_ADMIN.DEMO_GENERATOR.IMAGES_HINT', { max: MAX_IMAGES })}
                    </FieldDescription>
                    {images.length === 0 ? (
                      <p className="text-sm text-destructive">{t('SUPER_ADMIN.DEMO_GENERATOR.NO_IMAGES')}</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {images.map((imageUrl) => {
                          const order = chosen.indexOf(imageUrl);
                          const crop = crops[imageUrl];
                          return (
                            <div key={imageUrl} className="group relative aspect-square">
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => toggleImage(imageUrl)}
                                className={cn(
                                  'relative size-full overflow-hidden rounded-lg border bg-muted transition-opacity',
                                  order >= 0 ? 'outline outline-[1.5px] outline-foreground' : 'opacity-60 hover:opacity-100',
                                )}
                              >
                                <img
                                  src={imageUrl}
                                  alt=""
                                  referrerPolicy="no-referrer"
                                  loading="lazy"
                                  className={cn('size-full object-cover', crop && 'invisible')}
                                  onError={() => onImageError(imageUrl)}
                                  onLoad={(e) => {
                                    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                                    setNaturalSizes((prev) => (prev[imageUrl] ? prev : { ...prev, [imageUrl]: { w, h } }));
                                  }}
                                />
                                {crop && (
                                  <div className="absolute inset-0">
                                    <CroppedPreview src={imageUrl} crop={crop} natural={naturalSizes[imageUrl]} />
                                  </div>
                                )}
                                {order >= 0 && (
                                  <span className="absolute start-1.5 top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1 text-xs font-medium text-background">
                                    {order === 0 ? <Check className="size-3" /> : order + 1}
                                  </span>
                                )}
                                {order === 0 && (
                                  <Badge variant="secondary" className="absolute bottom-1.5 start-1.5">
                                    {t('SUPER_ADMIN.DEMO_GENERATOR.MAIN_IMAGE')}
                                  </Badge>
                                )}
                                {crop && (
                                  <Badge variant="secondary" className="absolute bottom-1.5 end-1.5">
                                    {t('SUPER_ADMIN.DEMO_GENERATOR.CROPPED')}
                                  </Badge>
                                )}
                              </button>
                              {order >= 0 && !busy && (
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="secondary"
                                  className="absolute end-1.5 top-1.5 size-7"
                                  title={t('SUPER_ADMIN.DEMO_GENERATOR.CROP')}
                                  aria-label={t('SUPER_ADMIN.DEMO_GENERATOR.CROP')}
                                  onClick={() => setCropping(imageUrl)}
                                >
                                  <Crop className="size-3.5" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Field>

                  {isSurface && (
                    <Field>
                      <FieldLabel>{t('SUPER_ADMIN.DEMO_GENERATOR.TILES')}</FieldLabel>
                      <FieldDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.TILES_HINT')}</FieldDescription>
                      {tilesRun?.phase === 'running' && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="size-4 animate-spin" />
                          {t('SUPER_ADMIN.DEMO_GENERATOR.TILES_RUNNING')}
                        </p>
                      )}
                      {tilesRun?.phase === 'error' && (
                        <p className="text-sm text-destructive">
                          {tilesRun.error} {t('SUPER_ADMIN.DEMO_GENERATOR.TILES_FALLBACK')}
                        </p>
                      )}
                      {tilesRun?.phase === 'result' && !tilesResult && (
                        <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.TILES_NONE')}</p>
                      )}
                      {tilesResult && (
                        <>
                          <p className="text-sm text-muted-foreground">
                            {tilesResult.mode === 'seamless'
                              ? t('SUPER_ADMIN.DEMO_GENERATOR.TILES_SEAMLESS')
                              : t('SUPER_ADMIN.DEMO_GENERATOR.TILES_FOUND', { count: tilesResult.images.length })}
                            {tilesResult.pattern && ` ${t('SUPER_ADMIN.DEMO_GENERATOR.TILES_PATTERN', { pattern: patternLabel(tilesResult.pattern) })}`}
                          </p>
                          <div className={cn('grid grid-cols-4 gap-2 sm:grid-cols-6', tilesStale && 'opacity-50')}>
                            {tilesResult.images.map((im, i) => {
                              const on = chosenTiles.has(i);
                              return (
                                <button
                                  key={im.url}
                                  type="button"
                                  disabled={busy}
                                  aria-pressed={on}
                                  onClick={() => toggleTile(i)}
                                  className={cn(
                                    'relative aspect-square overflow-hidden rounded-lg border bg-muted transition-opacity',
                                    on ? 'outline outline-[1.5px] outline-foreground' : 'opacity-50 hover:opacity-100',
                                  )}
                                >
                                  <img src={im.url} alt="" className="size-full object-contain" />
                                  {on && (
                                    <span className="absolute end-1 top-1 rounded-md bg-foreground p-0.5 text-background">
                                      <Check className="size-3" />
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                      {tilesStale && tilesRun?.phase !== 'running' && (
                        <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.TILES_STALE')}</p>
                      )}
                      {tilesRun && tilesRun.phase !== 'running' && (
                        <div>
                          <Button variant="outline" size="sm" onClick={() => void cutTiles()} disabled={busy || !chosen.length}>
                            <Sparkles data-icon="inline-start" />
                            {t('SUPER_ADMIN.DEMO_GENERATOR.TILES_AGAIN')}
                          </Button>
                        </div>
                      )}
                    </Field>
                  )}

                  {isSurface && chosen.length > 1 && tilesRun?.phase !== 'running' && !tilesForProduct() && (
                    <Field>
                      <FieldLabel>{t('SUPER_ADMIN.DEMO_GENERATOR.PLANKS')}</FieldLabel>
                      <FieldDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.PLANKS_HINT')}</FieldDescription>
                      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6" data-testid="plank-photos">
                        {chosen.map((imageUrl, i) => {
                          const laid = texturePhotos.length ? plankPhotos.has(imageUrl) : i === 0;
                          return (
                            <button
                              key={imageUrl}
                              type="button"
                              disabled={busy}
                              aria-pressed={laid}
                              aria-label={t('SUPER_ADMIN.DEMO_GENERATOR.PLANK_PHOTO', { n: i + 1 })}
                              onClick={() => togglePlankPhoto(imageUrl)}
                              className={cn(
                                'relative aspect-square overflow-hidden rounded-lg border bg-muted transition-opacity',
                                laid ? 'outline outline-[1.5px] outline-foreground' : 'opacity-50 hover:opacity-100',
                              )}
                            >
                              {crops[imageUrl] ? (
                                <CroppedPreview src={imageUrl} crop={crops[imageUrl]} natural={naturalSizes[imageUrl]} />
                              ) : (
                                <img src={imageUrl} alt="" referrerPolicy="no-referrer" loading="lazy" className="size-full object-cover" />
                              )}
                              {laid && (
                                <span className="absolute start-1 top-1 flex size-5 items-center justify-center rounded-full bg-foreground text-background">
                                  <Check className="size-3" />
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  )}

                  <PhotoCropDialog
                    src={cropping}
                    initial={cropping ? crops[cropping] : undefined}
                    onClose={() => setCropping(null)}
                    onApply={(crop) => cropping && applyCrop(cropping, crop)}
                  />

                  <ScrapedFacts scan={scan} />
                </>
              )}

              <VisualizerPlacementPicker
                options={scan.placement}
                value={placement}
                onChange={setPlacement}
                disabled={busy}
              />

              <Field orientation="horizontal">
                <Switch id="gen-ai-placement" checked={aiPlacement} onCheckedChange={setAiPlacement} disabled={busy} />
                <div>
                  <FieldLabel htmlFor="gen-ai-placement">{t('SUPER_ADMIN.DEMO_GENERATOR.AI_PLACEMENT')}</FieldLabel>
                  <FieldDescription>{t('SUPER_ADMIN.DEMO_GENERATOR.AI_PLACEMENT_HINT')}</FieldDescription>
                </div>
              </Field>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button variant="outline" onClick={generate} disabled={!canGenerate}>
                  {step === 'generating'
                    ? <Loader2 data-icon="inline-start" className="animate-spin" />
                    : <Sparkles data-icon="inline-start" />}
                  {step === 'generating' ? t('SUPER_ADMIN.DEMO_GENERATOR.GENERATING') : t('SUPER_ADMIN.DEMO_GENERATOR.GENERATE')}
                </Button>
                <Button variant="ghost" onClick={startOver} disabled={busy}>
                  <RotateCcw data-icon="inline-start" />
                  {t('SUPER_ADMIN.DEMO_GENERATOR.START_OVER')}
                </Button>
              </div>
              {step === 'generating' && (
                <p className="text-sm text-muted-foreground">
                  {t(usingExisting ? 'SUPER_ADMIN.DEMO_GENERATOR.GENERATING_HINT_EXISTING' : 'SUPER_ADMIN.DEMO_GENERATOR.GENERATING_HINT')}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* 3. The result */}
        {step === 'done' && result && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="size-5 text-success" />
                {t('SUPER_ADMIN.DEMO_GENERATOR.DONE_TITLE')}
              </CardTitle>
              <CardDescription>
                <a href={withTeamTag(result.url)} target="_blank" rel="noreferrer" className="font-medium text-foreground hover:underline">
                  {result.url.replace(/^https?:\/\//, '')}
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.RESULT_PAGE')}</dt>
                  <dd>
                    {t('SUPER_ADMIN.DEMO_GENERATOR.RESULT_PAGE_VALUE', { count: result.assetsSaved })}
                    {result.assetsSkipped > 0 && (
                      <span className="text-muted-foreground"> · {t('SUPER_ADMIN.DEMO_GENERATOR.RESULT_SKIPPED', { count: result.assetsSkipped })}</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.RESULT_SKU')}</dt>
                  <dd className="font-mono text-xs leading-5">{result.product.sku}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.RESULT_PRODUCT')}</dt>
                  <dd className="flex items-center gap-1.5">
                    {!result.batchId ? (
                      <>
                        <CheckCircle2 className="size-3.5 text-success" />
                        {t('SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_EXISTING', { name: result.product.name })}
                      </>
                    ) : !productStatus || productStatus.status === 'pending' || productStatus.status === 'processing' ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                        {t(family?.visualization === 'surface'
                          ? 'SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_PROCESSING_SURFACE'
                          : 'SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_PROCESSING')}
                      </>
                    ) : productStatus.status === 'completed' ? (
                      <>
                        <CheckCircle2 className="size-3.5 text-success" />
                        {t('SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_READY')}
                      </>
                    ) : (
                      <>
                        <XCircle className="size-3.5 text-destructive" />
                        <span className="text-destructive">{productStatus.error || t('SUPER_ADMIN.DEMO_GENERATOR.PRODUCT_FAILED')}</span>
                      </>
                    )}
                  </dd>
                </div>
                {result.placement && (
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.RESULT_PLACEMENT')}</dt>
                    <dd>
                      {t(`SUPER_ADMIN.DEMO_GENERATOR.AI_PLACEMENT_STATUS_${result.placement.ai.status.toUpperCase()}`)}
                      {result.placement.ai.reason && <span className="text-muted-foreground"> · {result.placement.ai.reason}</span>}
                      {result.placement.ai.error && <span className="text-muted-foreground"> · {result.placement.ai.error}</span>}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.RESULT_CAPTURE')}</dt>
                  <dd>
                    {t(result.renderedWith === 'browser'
                      ? 'SUPER_ADMIN.DEMO_GENERATOR.CAPTURE_BROWSER'
                      : 'SUPER_ADMIN.DEMO_GENERATOR.CAPTURE_HTML')}
                  </dd>
                </div>
              </dl>
              {result.previewUrl && (
                <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.PREVIEW_HINT')}</p>
              )}
              <ProspectResult prospect={result.prospect} />
              {result.batchId && isRug && (
                <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.RUG_MODEL_NOTE')}</p>
              )}
              {result.batchId && family?.visualization === 'model3d' && !isRug && (
                <p className="text-sm text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.MODEL3D_NOTE')}</p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" asChild>
                  <a href={withTeamTag(result.url)} target="_blank" rel="noreferrer">
                    <ExternalLink data-icon="inline-start" />
                    {t('SUPER_ADMIN.DEMO_GENERATOR.OPEN_DEMO')}
                  </a>
                </Button>
                {result.previewUrl && (
                  <Button variant="outline" asChild>
                    <a href={result.previewUrl} target="_blank" rel="noreferrer" title={t('SUPER_ADMIN.DEMO_GENERATOR.PREVIEW_HINT')}>
                      <Eye data-icon="inline-start" />
                      {t('SUPER_ADMIN.DEMO_GENERATOR.PREVIEW')}
                    </a>
                  </Button>
                )}
                <Button variant="ghost" onClick={startOver}>
                  <RotateCcw data-icon="inline-start" />
                  {t('SUPER_ADMIN.DEMO_GENERATOR.GENERATE_ANOTHER')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Toast
        message={toast?.msg || ''}
        type={toast?.sev || 'success'}
        isVisible={!!toast}
        onClose={() => setToast(null)}
      />
    </PageContainer>
  );
};

/** One option in a pick-one list; the selected one is outlined. */
const ChoiceRow: React.FC<{ selected: boolean; disabled?: boolean; onSelect: () => void; children: React.ReactNode }> = ({
  selected, disabled, onSelect, children,
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onSelect}
    className={cn(
      'flex min-h-12 items-center gap-3 rounded-lg border p-2 text-start transition-colors hover:bg-muted/50',
      selected && 'outline outline-[1.5px] outline-foreground',
    )}
  >
    <div className="min-w-0 flex-1">{children}</div>
    {selected && <CheckCircle2 className="size-4 shrink-0" />}
  </button>
);

/** A product already in the demo catalogue: thumbnail, name, SKU. */
const ExistingProduct: React.FC<{ product: DemoProduct }> = ({ product: p }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {p.thumbnailPath
          ? <img src={p.thumbnailPath} alt="" className="size-full object-cover" />
          : <ImageOff className="size-4 text-muted-foreground" />}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{p.name || '—'}</div>
        <div className="truncate text-xs text-muted-foreground">
          <span className="font-mono">{p.sku || t('SUPER_ADMIN.DEMO_GENERATOR.NO_SKU_SHORT')}</span>
          {p.variants > 1 && ` · ${t('SUPER_ADMIN.DEMO_GENERATOR.VARIANTS', { count: p.variants })}`}
          {p.isDraft && ` · ${t('SUPER_ADMIN.DEMO_GENERATOR.DRAFT')}`}
        </div>
      </div>
    </div>
  );
};

/** What else the extractor found, which goes into the product as it is. */
/**
 * The prospect the demo was made for. On its first demo, its login: the
 * password is shown this once — it is stored only as a hash.
 */
const ProspectResult: React.FC<{ prospect: GeneratedDemo['prospect'] }> = ({ prospect }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<'email' | 'password' | null>(null);
  if (!prospect) return null;
  const copy = async (what: 'email' | 'password', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 1500);
    } catch { /* the value is on screen to copy by hand */ }
  };
  const login = prospect.login;
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3 text-sm" data-testid="demo-prospect">
      <p>
        {t(prospect.created ? 'SUPER_ADMIN.DEMO_GENERATOR.PROSPECT_CREATED' : 'SUPER_ADMIN.DEMO_GENERATOR.PROSPECT_REUSED', { vendor: prospect.name, alias: prospect.alias })}
      </p>
      {login && (
        <>
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {(['email', 'password'] as const).map((key) => (
              <div key={key}>
                <dt className="text-muted-foreground">{t(key === 'email' ? 'SUPER_ADMIN.DEMO_GENERATOR.PROSPECT_EMAIL' : 'SUPER_ADMIN.DEMO_GENERATOR.PROSPECT_PASSWORD')}</dt>
                <dd className="flex items-center gap-2">
                  <span className="font-mono text-xs">{login[key]}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copy(key, login[key])}>
                    {copied === key ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
                    {t(copied === key ? 'SUPER_ADMIN.DEMO_GENERATOR.COPIED' : 'SUPER_ADMIN.DEMO_GENERATOR.COPY')}
                  </Button>
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-muted-foreground">{t('SUPER_ADMIN.DEMO_GENERATOR.PROSPECT_LOGIN_HINT', { url: window.location.origin })}</p>
        </>
      )}
    </div>
  );
};

const ScrapedFacts: React.FC<{ scan: DemoScan }> = ({ scan }) => {
  const { t } = useTranslation();
  const p = scan.product;
  const facts: [string, React.ReactNode][] = [];
  if (p.brand) facts.push([t('SUPER_ADMIN.DEMO_GENERATOR.FACT_BRAND'), p.brand]);
  if (p.size?.width && p.size?.height) {
    facts.push([t('SUPER_ADMIN.DEMO_GENERATOR.FACT_SIZE'), `${p.size.width} × ${p.size.height}${p.size.depth ? ` × ${p.size.depth}` : ''} ${p.size.unit || 'cm'}`]);
  }
  if (p.color?.value) {
    facts.push([t('SUPER_ADMIN.DEMO_GENERATOR.FACT_COLOR'), (
      <span className="inline-flex items-center gap-1.5">
        {p.color.hex && <span className="size-3 rounded-full border" style={{ backgroundColor: p.color.hex }} />}
        {p.color.value}
      </span>
    )]);
  }
  if (p.pattern) facts.push([t('SUPER_ADMIN.DEMO_GENERATOR.FACT_PATTERN'), p.pattern]);
  if (p.productType) facts.push([t('SUPER_ADMIN.DEMO_GENERATOR.FACT_TYPE'), p.productType]);
  if (p.price) facts.push([t('SUPER_ADMIN.DEMO_GENERATOR.FACT_PRICE'), p.price]);
  if (!facts.length) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border p-3 text-sm sm:grid-cols-3">
      {facts.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
};

export default DemoGeneratorPage;
